/**
 * Quick-add for the cart drawer's "You may also like" tiles.
 *
 * - One purchasable variant: a single tap adds it.
 * - Several: the button swaps to a picker in the same slot, so the tile never
 *   changes height and the carousel does not reflow. Choosing brings the
 *   button back, labelled with the chosen option, ready to add.
 * - The add request asks Shopify for the re-rendered header and cart drawer in
 *   the same response (the pattern this theme already uses for its product
 *   forms), so the bag count and totals update without a second round trip.
 * - The cross-sell carousel is re-fetched by the theme after every cart change,
 *   which resets its scroll position. We put it back where the shopper left it.
 */
(() => {
  const SECTION_ELEMENTS = ['page-header', 'cart-drawer'];
  const pendingScroll = new WeakMap();

  /** Remember where the cross-sell carousel is scrolled to, and put it back
   *  once the theme swaps in freshly rendered tiles. */
  function preserveCarouselPosition(el) {
    const host = el.closest('cc-cart-cross-sell');
    if (!host) return;
    const slider = host.querySelector('.slider');
    if (!slider || !slider.scrollLeft) return;

    pendingScroll.set(host, slider.scrollLeft);

    if (host.quickAddObserver) return;
    host.quickAddObserver = new MutationObserver(() => {
      const left = pendingScroll.get(host);
      if (left == null) return;
      pendingScroll.delete(host);
      const restore = () => {
        const current = host.querySelector('.slider');
        if (current) current.scrollLeft = left;
      };
      requestAnimationFrame(() => {
        restore();
        // The carousel re-measures itself after swapping content in.
        setTimeout(restore, 80);
      });
    });
    host.quickAddObserver.observe(host, { childList: true, subtree: true });
  }

  let liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
  }

  function errorMessageFrom(data) {
    if (data && data.errors && typeof data.errors === 'object') {
      return Object.values(data.errors)
        .map((value) => (Array.isArray(value) ? value.join(', ') : value))
        .join(', ');
    }
    if (data && typeof data.description === 'string') return data.description;
    if (data && typeof data.message === 'string') return data.message;
    return 'Sorry, that could not be added.';
  }

  class CartQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.button = this.querySelector('.cart-quick-add__btn');
      this.select = this.querySelector('.cart-quick-add__select');
      if (!this.button) return;

      this.productTitle = this.dataset.productTitle || 'Item';
      this.button.addEventListener('click', this.handleClick.bind(this));

      if (this.select) {
        this.select.addEventListener('click', (evt) => evt.stopPropagation());
        this.select.addEventListener('change', this.handleChoice.bind(this));
        this.select.addEventListener('blur', () => {
          // Dismissed without choosing - put the button back so the tile is
          // never left stranded in the picker state.
          if (!this.select.value) window.setTimeout(() => this.showButton('Add'), 120);
        });
      }
    }

    showPicker() {
      this.select.hidden = false;
      this.button.hidden = true;
      this.select.focus({ preventScroll: true });
    }

    showButton(label) {
      if (this.select) this.select.hidden = true;
      this.button.hidden = false;
      this.setLabel(label, false);
    }

    handleChoice() {
      const chosen = this.select.options[this.select.selectedIndex];
      const name = chosen && chosen.value ? chosen.textContent.trim() : '';
      this.showButton(name ? `Add ${name}` : 'Add');
      this.button.setAttribute('aria-label', `Add ${this.productTitle}${name ? `, ${name}` : ''} to bag`);
      this.button.focus({ preventScroll: true });
    }

    setLabel(text, busy) {
      this.button.textContent = text;
      this.button.disabled = Boolean(busy);
      if (busy) {
        this.button.setAttribute('aria-busy', 'true');
      } else {
        this.button.removeAttribute('aria-busy');
      }
    }

    handleClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (this.busy) return;

      if (this.select && this.select.hidden && !this.select.value) {
        this.showPicker();
        return;
      }

      const variantId = this.select ? this.select.value : this.button.dataset.variantId;
      if (!variantId) {
        this.showPicker();
        return;
      }
      this.addToCart(variantId);
    }

    async addToCart(variantId) {
      this.busy = true;
      preserveCarouselPosition(this);
      this.setLabel('Adding', true);

      const sections = SECTION_ELEMENTS
        .map((selector) => document.querySelector(selector))
        .filter((el) => el && typeof el.updateFromCartChange === 'function' && el.dataset.sectionId);
      const sectionIds = sections.map((el) => el.dataset.sectionId);

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);
      if (sectionIds.length) {
        body.append('sections', sectionIds.join(','));
        body.append('sections_url', window.location.pathname);
      }

      const addUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await response.json();
        if (!response.ok) throw new Error(errorMessageFrom(data));

        this.button.classList.add('cart-quick-add__btn--added');
        this.setLabel('Added', true);
        announce(`${this.productTitle} added to your bag`);

        const haveAllSections = data.sections
          && sectionIds.length
          && sectionIds.every((id) => typeof data.sections[id] === 'string');

        if (haveAllSections) {
          sections.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        }
      } catch (error) {
        this.button.classList.add('cart-quick-add__btn--error');
        this.setLabel('Try again', false);
        announce(error.message);
        if (window.theme && typeof theme.showQuickPopup === 'function') {
          theme.showQuickPopup(error.message, this.button);
        }
        // The cart can still have changed, so re-sync rather than trust the failure.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      } finally {
        this.busy = false;
      }
    }
  }

  if (!window.customElements.get('cart-quick-add')) {
    window.customElements.define('cart-quick-add', CartQuickAdd);
  }
})();
